import { useEffect, useState } from "react";
import { DB_ERROR } from "../util/helper/constants";
import { getLocalStorageItem } from "../util/helper/helperFunctions";

const useSocketData = (socket, channel, shouldSubscribe = true) => {
  const [data, setData] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const auth = getLocalStorageItem("auth");

  useEffect(() => {
    if (!auth || !socket || !channel || !shouldSubscribe) return;

    const handleData = ({ results }) => {
      if (results && results !== DB_ERROR) {
        setData(results);
        setConnectionError(false);
      } else {
        setConnectionError(true);
      }
    };

    socket.on(channel, handleData);
    return () => {
      socket.off(channel, handleData);
    };
  }, [socket, channel, shouldSubscribe, auth]);

  connectionError &&
    console.error(
      `From Socket Hook :: connection error for channel ", ${channel}`
    );

  return { data, connectionError };
};

export default useSocketData;

/*
  Purpose: 
  useSocketEffect is a custom hook that internally uses useSocketData.
  It handles socket subscription and invokes a callback (e.g., setState or dispatch)
  when valid data is received, avoiding repetitive useEffect patterns.

  Example:
    // Instead of:
    const { data, connectionError } = useSocketData(socket, channel);
    useEffect(() => {
      if (data) setData(data);
    }, [data]);

    // Use:
    useSocketDataEffect({
      socket,
      channel,
      onData: state setterFn or store dispatch
      shouldSubscribe : defaults to true, conditionally subscribe data from channel Eg: fetchs data of yard view only yard view is enabled
    });
*/

const defaultDataValidator = (data) => {
  if (data !== DB_ERROR && data) {
    if (Array.isArray(data)) return data.length > 0;
    else if (typeof data === "object") return Object.keys(data).length > 0;
  }
  return false;
};

export const useSocketDataEffect = ({
  socket,
  channel,
  onData,
  validate = defaultDataValidator, //default logic, pass custom validation fn as argument
  shouldSubscribe = true
}) => {
  const { data, connectionError } = useSocketData(
    socket,
    channel,
    shouldSubscribe
  );

  useEffect(() => {
    if (!connectionError && validate(data)) {
      onData(data);
    }
  }, [data, connectionError]);

  return { data, connectionError };
};
